import { createColumnHelper } from '@tanstack/react-table';

import type { AccountDTO, LocationDTO } from '@invenco/accounts-interface';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { toTitleCase } from '@/shared/helpers';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';
import { locationStatusType } from '@/shared/status-maps';
import { locationStatusTitle } from '@/shared/title-maps';

import { InviteToWarehouseDialogContent } from './invite-to-warehouse-dialog-content';

type Props = {
  account: Partial<AccountDTO>;
  isLoading?: boolean;
  isNew?: boolean;
  onInviteComplete?: () => void;
};

type LocationRow = LocationDTO;

const columnHelper = createColumnHelper<LocationRow>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? toTitleCase(String(value)) : null;
    },
  }),
  columnHelper.accessor('warehouseLocationName', {
    header: 'Warehouse location',
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? (
        <StatusBadge status={value} type={locationStatusType} title={locationStatusTitle} />
      ) : null;
    },
  }),
];

export function LocationsCard({ account, isLoading, isNew, onInviteComplete }: Props) {
  const { warehousingGateway } = useGateways();
  const messages = useMessages();
  const accountId = account.id;
  const locations = account.locations ?? [];
  const { state, onSortingChange } = useDataTableState({ columns, tableId: 'account-locations' });
  const onClickRow = useRowNavigationCallback<LocationRow>('/locations');
  const canInvite = !isNew && Boolean(accountId);

  const { mutate: addToWarehouse } = useGatewayMutation({
    mutationFn: ({
      warehouseId,
      tenantName,
      tenantCode,
    }: {
      warehouseId: string;
      tenantName: string;
      tenantCode: string;
    }) =>
      warehousingGateway.createWarehouseTenant(warehouseId, {
        tenantAccountId: accountId!,
        tenantName,
        tenantCode,
        warehouseTenantOptions: {},
      }),
    onSuccess: (tenant) => {
      onInviteComplete?.();
      messages.success(`Added account to ${tenant.warehouse?.name ?? 'warehouse'}`);
    },
    linkedQuery: ['locations'],
    linkResponseToQuery: false,
  });

  const table = useDataTable({
    data: locations,
    columns,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
  });

  return (
    <Card aria-label="Locations">
      <CardHeader>
        <DataTableCardTitle table={table}>Locations</DataTableCardTitle>
        {canInvite ? (
          <CardAction>
            <FormDialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  Invite to warehouse
                </Button>
              </DialogTrigger>
              <InviteToWarehouseDialogContent
                account={account}
                onSubmit={async (data) => addToWarehouse(data)}
              />
            </FormDialog>
          </CardAction>
        ) : null}
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} onClickRow={onClickRow} />
      </DataTableCardBody>
    </Card>
  );
}
